import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import ExcelIcon from '../../../assets/ms_excel.svg';
import { useFetchData } from '../../../hooks/useFetchData';
import { editProject, generateDataRequestListReport } from '../../../services/dealGPT';
import { StyledIconButton } from '../../../theme/CustomComponents';
import { DataRequestItem, ProjectWithLastScanned } from '../../../Types/dealGPT';
import { EventSource, HistoryViewMode } from '../../../Types/enums';
import { downloadExcel } from '../../../util/excel';
import { dataRequestListPathUrl } from '../../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useDrawer } from '../../Providers/DrawerProvider';
import EventHistory, { EventHistoryRef } from '../EventHistory';
import DataRequestListDrawer from './DataRequestListDrawer';
import { DataRequestListReIndexButton } from './DataRequestListReIndexButton';
import DataRequestListTable from './DataRequestListTable';
import { createDataRequestListExcelReport } from './excel.report';
import ProjectActiveDates from './ProjectActiveDates';

interface DataRequestListProps {
  project: ProjectWithLastScanned;
  refetchProject: () => Promise<void>;
}

const DataRequestList = ({ project, refetchProject }: DataRequestListProps): JSX.Element => {
  const analytics = useAnalytics();
  const { openDrawer } = useDrawer();
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'key', sort: 'asc' }]);
  const eventHistoryRef = useRef<EventHistoryRef>(null);

  const {
    data,
    pagination,
    setPagination,
    refetch: refetchData,
  } = useFetchData<DataRequestItem>({
    urlPath: dataRequestListPathUrl(project.id, selectedScopes, sortModel),
    paginated: true,
  });

  const handleItemChange = useCallback(async () => {
    await refetchData();
    eventHistoryRef.current?.refetch();
  }, [refetchData]);

  const scopes = useMemo(() => project.scopes?.map((s) => s.scope) ?? [], [project.scopes]);

  const toggleScope = useCallback((scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  }, []);

  const handleGenerateReport = useCallback(async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_DATA_REQUEST_LIST_EXPORT,
      {
        id: project.id,
      },
      {
        projectId: project.id,
        selectedScopes,
      }
    );

    const response = await generateDataRequestListReport(project.id, selectedScopes, sortModel);

    if (response?.total === 0) {
      enqueueSnackbar('No data found to generate report', { variant: 'error' });
      return;
    }

    const workbook = createDataRequestListExcelReport(response.items);
    downloadExcel(
      workbook,
      `Intellio®-Advantage-${project.name}-${new Date().toISOString()}-DRL.xlsx`
    );
  }, [analytics, project.id, selectedScopes, sortModel, project.name]);

  const updateProjectDate = useCallback(
    (field: string, date: Date | undefined) => {
      editProject({
        id: project.id,
        status: project.status,
        name: project.name,
        description: project.description,
        projectType: project.projectType,
        teamsId: project.teamsId,
        clientName: project.clientName,
        indexToUse: project.indexToUse,
        [field]: date,
      });
    },
    [project]
  );

  return (
    <Stack direction="column" spacing={2} width="100%" height="100%">
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        Data Request List
      </Typography>
      <Stack direction="row" spacing={1}>
        {scopes.map((scope) => (
          <Chip
            key={scope}
            component="span"
            label={scope}
            sx={{
              color: '#070154',
              backgroundColor: selectedScopes.includes(scope) ? '#E8EEF8' : undefined,
              '&:hover': {
                backgroundColor: selectedScopes.includes(scope) ? '#E8EEF8' : undefined,
              },
            }}
            onClick={() => toggleScope(scope)}
          />
        ))}
      </Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ProjectActiveDates
          project={project}
          onStartDateChange={(date) => updateProjectDate('activeStartDate', date)}
          onEndDateChange={(date) => updateProjectDate('activeEndDate', date)}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="Add or remove Data Request List Items">
            <StyledIconButton
              onClick={() =>
                openDrawer({
                  anchor: 'right',
                  content: (
                    <DataRequestListDrawer
                      projectId={project.id}
                      onSave={() => {
                        refetchProject();
                        handleItemChange();
                      }}
                    />
                  ),
                  size: 'md',
                })
              }
            >
              <EditNoteRoundedIcon />
            </StyledIconButton>
          </Tooltip>
          <DataRequestListReIndexButton
            project={project}
            refetchProject={refetchProject}
            refetchDataRequestList={handleItemChange}
          />
          <Button
            variant="outlined"
            onClick={handleGenerateReport}
            startIcon={<img src={ExcelIcon} alt="Excel" style={{ width: 20, height: 20 }} />}
          >
            Export
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          gap: 2,
          display: 'flex',
          flexDirection: 'row',
          height: 'calc(100% - 200px)', // Total Remaining space - header and filters
        }}
      >
        <DataRequestListTable
          data={data}
          pagination={pagination}
          setPagination={setPagination}
          refetchData={handleItemChange}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
        />
        <Box
          borderRadius="10px"
          display="flex"
          flexDirection="column"
          gap={1}
          border="1px solid #CED7E6"
          padding={2}
          overflow="auto"
        >
          <Typography variant="subtitle1">Recent Activity</Typography>
          <EventHistory
            ref={eventHistoryRef}
            projectId={project.id}
            entityId={project.id}
            entityType={EventSource.PROJECT}
            viewMode={HistoryViewMode.COMPACT}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default DataRequestList;
